import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  EngFinding,
  EngReview,
  EngReviewAssignment,
  EngReviewer,
} from "../types";

const BASE = "/eng-aron";
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export interface AsyncTaskRecord {
  id: string;
  status: "pending" | "running" | "success" | "failure" | "cancelled";
  log: string;
  result: unknown;
  error: string;
}

const TERMINAL_TASK = ["success", "failure", "cancelled"];
const ACTIVE_REVIEW = ["queued", "running"];

export const reviewKeys = {
  list: (filters: Record<string, string>) => ["eng-aron", "reviews", filters] as const,
  detail: (id: string) => ["eng-aron", "review", id] as const,
  findings: (id: string) => ["eng-aron", "review", id, "findings"] as const,
  emailText: (id: string) => ["eng-aron", "review", id, "email-text"] as const,
  task: (id: string) => ["eng-aron", "task", id] as const,
  history: (filters: Record<string, string>) => ["eng-aron", "findings", filters] as const,
};

function clean(obj: Record<string, string | undefined>) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
}

export interface ReviewModeInfo {
  value: string;
  label: string;
  cost: string;
  models: string;
  effort: string;
  est_usd_per_100p: string;
}

export function useReviewModesInfo() {
  return useQuery({
    queryKey: ["eng-aron", "review-modes"],
    queryFn: () => unwrap<ReviewModeInfo[]>(apiClient.get(`${BASE}/review-modes/`)),
    staleTime: 10 * 60_000,
  });
}

export function useReviews(filters: Record<string, string>) {
  return useQuery({
    queryKey: reviewKeys.list(filters),
    queryFn: () =>
      unwrap<EngReview[]>(apiClient.get(`${BASE}/reviews/`, { params: clean(filters) })),
    refetchInterval: (q) => {
      const rows = q.state.data as EngReview[] | undefined;
      return rows?.some((r) => ACTIVE_REVIEW.includes(r.status)) ? 4000 : false;
    },
  });
}

export function useReview(id: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.detail(id ?? ""),
    queryFn: () => unwrap<EngReview>(apiClient.get(`${BASE}/reviews/${id}/`)),
    enabled: !!id,
    refetchInterval: (q) => {
      const r = q.state.data as EngReview | undefined;
      return r && ACTIVE_REVIEW.includes(r.status) ? 2000 : false;
    },
  });
}

export function useReviewFindings(id: string | undefined, reviewStatus?: string) {
  return useQuery({
    queryKey: reviewKeys.findings(id ?? ""),
    queryFn: () =>
      unwrap<EngFinding[]>(apiClient.get(`${BASE}/reviews/${id}/findings/`)),
    enabled: !!id && !!reviewStatus && !ACTIVE_REVIEW.includes(reviewStatus),
  });
}

export function useReviewEmailText(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: reviewKeys.emailText(id ?? ""),
    queryFn: () =>
      unwrap<{ text: string }>(apiClient.get(`${BASE}/reviews/${id}/email-text/`)),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function useTaskPolling(taskId: string | null) {
  return useQuery<AsyncTaskRecord>({
    queryKey: reviewKeys.task(taskId ?? ""),
    queryFn: () => unwrap<AsyncTaskRecord>(apiClient.get(`/bg-tasks/${taskId}/`)),
    enabled: !!taskId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && TERMINAL_TASK.includes(s) ? false : 1500;
    },
  });
}

function useInvalidateReviews() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["eng-aron"] });
}

export function useCreateReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (vars: {
      project: string | null;
      label: string;
      branch: string;
      doc_type: string;
      mode: string;
      checker_notes: string;
      file: File;
    }) => {
      const fd = new FormData();
      if (vars.project) fd.append("project", vars.project);
      fd.append("label", vars.label);
      fd.append("branch", vars.branch);
      fd.append("doc_type", vars.doc_type);
      fd.append("mode", vars.mode);
      fd.append("checker_notes", vars.checker_notes);
      fd.append("file", vars.file);
      return unwrap<EngReview>(apiClient.post(`${BASE}/reviews/`, fd, MULTIPART));
    },
    onSuccess: invalidate,
  });
}

export function useNewVersionReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      file: File;
      mode?: string;
      checker_notes?: string;
      skip_ai?: boolean;
    }) => {
      const fd = new FormData();
      fd.append("file", vars.file);
      if (vars.mode) fd.append("mode", vars.mode);
      if (vars.checker_notes !== undefined) fd.append("checker_notes", vars.checker_notes);
      if (vars.skip_ai) fd.append("skip_ai", "true");
      return unwrap<EngReview>(apiClient.post(`${BASE}/reviews/${vars.id}/new-version/`, fd, MULTIPART));
    },
    onSuccess: invalidate,
  });
}

export function useStartReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<{ task_id: string; review_id: string }>(
        apiClient.post(`${BASE}/reviews/${id}/start/`, {}),
      ),
    onSuccess: invalidate,
  });
}

export function useCancelReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<{ cancelled: boolean }>(apiClient.post(`${BASE}/reviews/${id}/cancel/`, {})),
    onSuccess: invalidate,
  });
}

export function useDeleteReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/reviews/${id}/`);
    },
    onSuccess: invalidate,
  });
}

export function useFindingsHistory(filters: Record<string, string>) {
  return useQuery({
    queryKey: reviewKeys.history(filters),
    queryFn: () =>
      unwrap<EngFinding[]>(apiClient.get(`${BASE}/findings/`, { params: clean(filters) })),
  });
}

export function usePatchFinding() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      status?: string;
      severity?: string;
      body?: string;
      dismissed_reason?: string;
    }) => unwrap<EngFinding>(apiClient.patch(`${BASE}/findings/${id}/`, payload)),
    onSuccess: invalidate,
  });
}

export function usePromoteFinding() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      unwrap<unknown>(apiClient.post(`${BASE}/findings/${id}/promote/`, payload)),
    onSuccess: invalidate,
  });
}

// ── Human review workflow ────────────────────────────────────────────────────

interface ReviewersInfo {
  assignable: EngReviewer[];
  assigned: EngReviewAssignment[];
}

export function useReviewReviewers(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["eng-aron", "review", id ?? "", "reviewers"],
    queryFn: () => unwrap<ReviewersInfo>(apiClient.get(`${BASE}/reviews/${id}/reviewers/`)),
    enabled: !!id && enabled,
  });
}

export function useShareReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({ id, reviewer_ids }: { id: string; reviewer_ids: string[] }) =>
      unwrap<EngReview>(apiClient.post(`${BASE}/reviews/${id}/reviewers/`, { reviewer_ids })),
    onSuccess: invalidate,
  });
}

export function useDecideReview() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: string; note?: string }) =>
      unwrap<EngReview>(apiClient.post(`${BASE}/reviews/${id}/decide/`, { decision, note })),
    onSuccess: invalidate,
  });
}

export function useAddManualFinding() {
  const invalidate = useInvalidateReviews();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      body: string;
      category?: string;
      severity?: string;
    }) => unwrap<EngFinding>(apiClient.post(`${BASE}/reviews/${id}/add-finding/`, payload)),
    onSuccess: invalidate,
  });
}

export interface MyReviews {
  to_review: EngReview[];
  done: EngReview[];
}

export function useMyReviews(enabled = true) {
  return useQuery({
    queryKey: ["eng-aron", "reviews", "mine"],
    queryFn: () => unwrap<MyReviews>(apiClient.get(`${BASE}/reviews/mine/`)),
    enabled,
  });
}
